import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt } from "lucide-react";

const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (d) => String(d || "").slice(0, 16).replace("T", " ");

function SaleDialog({ t, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="epos-sale-dialog">
        <DialogHeader><DialogTitle>{t.ref}{t.test_mode ? " (test)" : ""}</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 space-y-1">
          <div>{when(t.created_at)} · {(t.status || "").replace("_", " ")} · {t.customer?.name || "Walk-in"}</div>
          <div className="divide-y divide-slate-100 border-y border-slate-100 my-2">
            {(t.items || []).map((it, i) => (
              <div key={i} className="flex justify-between py-1.5" data-testid="epos-sale-item"><span>{it.qty} × {it.title}</span><span className="tabular-nums">{gbp(it.final_gross ?? it.line_gross)}</span></div>))}
          </div>
          <div className="flex justify-between font-semibold text-slate-900"><span>Total</span><span data-testid="epos-sale-total">{gbp(t.total)}</span></div>
          <div className="flex justify-between text-xs"><span>of which VAT</span><span>{gbp(t.vat_total)}</span></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EposSales() {
  const [params, setParams] = useSearchParams();
  const ref = params.get("ref") || "";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get("/epos/transactions", { params: { q } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (ref) { api.get("/epos/transactions", { params: { ref } }).then((r) => setOpen(r.data.items[0] || null)).catch(() => {}); } }, [ref]);
  const close = () => { setOpen(null); if (ref) { setParams({}); } };
  return (
    <div className="w-full px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="epos-sales">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5 mr-auto"><PageIcon /> Shop Sales</h1>
        <Input className="w-64" placeholder="Search ref, customer or item" value={q} onChange={(e) => setQ(e.target.value)} data-testid="epos-sales-search" />
      </div>
      {items === null ? <div className="text-slate-400 text-sm py-12 text-center">Loading…</div> : !items.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-slate-500" data-testid="epos-sales-empty"><Receipt className="w-8 h-8 mx-auto mb-3 text-slate-300" />No sales found.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500"><tr>{["Ref", "Date", "Customer", "Items", "Status", "Total"].map((h) => <th key={h} className="px-4 py-2">{h}</th>)}</tr></thead>
            <tbody>{items.map((t) => (
              <tr key={t.id} onClick={() => setOpen(t)} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`epos-sale-${t.ref}`}>
                <td className="px-4 py-2 font-mono font-semibold">{t.ref}{t.test_mode && <span className="ml-2 rounded bg-amber-100 px-1.5 text-[10px] text-amber-800">TEST</span>}</td>
                <td className="px-4 py-2 whitespace-nowrap">{when(t.created_at)}</td>
                <td className="px-4 py-2">{t.customer?.name || "Walk-in"}</td>
                <td className="px-4 py-2">{(t.items || []).length}</td>
                <td className="px-4 py-2">{(t.status || "").replace("_", " ")}</td>
                <td className="px-4 py-2 text-right tabular-nums">{gbp(t.total)}</td>
              </tr>))}</tbody>
          </table>
        </div>)}
      {open && <SaleDialog t={open} onClose={close} />}
    </div>
  );
}
